import { useSearchParams } from 'react-router-dom';

import FilterChips from '../../../common/components/filter-chips/filter-chips';
import ListRow from '../../../common/components/list-row/list-row';
import PageHeader from '../../../common/components/page-header/page-header';
import RelativeTime from '../../../common/components/relative-time/relative-time';
import StatusPill from '../../../common/components/status-pill/status-pill';
import EmptyState from '../../../common/components/state-message/empty-state';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import { WORKFLOW_STATE_LABEL, WORKFLOW_STATE_TONE } from '../../../core/constants/domain';
import { ROUTES } from '../../../core/constants/routes';
import { nameOf, useProjectCatalog } from '../../../global/contexts/catalogs/catalogs';
import { useTaskReviewRounds, useTasksList } from '../../../global/contexts/work/work';

import '../styles/queue.css';

const VIEWS = [
    { value: 'in_review', label: 'Esperando decisión' },
    { value: 'returned', label: 'Devueltas' },
    { value: 'done', label: 'Aceptadas' },
];

const EMPTY_COPY = {
    in_review: ['Nada que revisar', 'Cuando un agente entregue, la ronda aparecerá aquí.'],
    returned: ['Sin devoluciones', 'Ninguna entrega está esperando correcciones.'],
    done: ['Sin entregas aceptadas', 'Las tareas aceptadas aparecerán aquí.'],
};

function RoundSummary({ taskId }) {
    const { data } = useTaskReviewRounds(taskId);
    const round = data?.items[0];
    if (!round) return null;
    return (
        <span className="review-queue__summary">
            Ronda {round.number}:{' '}
            <span aria-hidden="true">{round.summary.cumple}✓ {round.summary.incumple}✗ {round.summary.no_comprobado}?</span>
            <span className="visually-hidden">
                {` (${round.summary.cumple} cumplen, ${round.summary.incumple} incumplen, ${round.summary.no_comprobado} sin comprobar)`}
            </span>
        </span>
    );
}

export default function ReviewQueue() {
    const [params, setParams] = useSearchParams();
    const requested = params.get('view');
    const view = VIEWS.some((option) => option.value === requested) ? requested : 'in_review';
    const { data, isPending, isError, error, refetch } = useTasksList({ workflowState: view });
    const { data: projects } = useProjectCatalog();
    const tasks = data?.items ?? [];
    const [emptyTitle, emptyDescription] = EMPTY_COPY[view];

    return (
        <div className="review-queue">
            <PageHeader
                eyebrow="Calidad"
                title="Revisiones"
                description="Rondas entregadas por los agentes con sus hallazgos por criterio. Acepta o devuelve con un motivo."
            />
            <FilterChips
                label="Vista de revisiones"
                options={VIEWS}
                value={view}
                onChange={(value) => setParams(value === 'in_review' ? {} : { view: value }, { replace: true })}
            />

            {isPending && <LoadingState label="Cargando revisiones" rows={3} />}
            {isError && <ErrorState error={error} onRetry={refetch} />}
            {!isPending && !isError && tasks.length === 0 && <EmptyState title={emptyTitle} description={emptyDescription} />}
            {tasks.length > 0 && (
                <ul className="review-queue__list">
                    {tasks.map((task) => (
                        <ListRow
                            key={task.id}
                            to={view === 'in_review' ? ROUTES.REVIEW(task.id) : ROUTES.TASK(task.id)}
                            tone={WORKFLOW_STATE_TONE[task.state]}
                            eyebrow={`#${task.id} · ${nameOf(projects, task.projectId, 'Proyecto')}`}
                            title={task.title}
                            meta={(
                                <>
                                    <StatusPill tone={WORKFLOW_STATE_TONE[task.state]}>{WORKFLOW_STATE_LABEL[task.state]}</StatusPill>
                                    <RoundSummary taskId={task.id} />
                                    <span><RelativeTime value={task.updatedAt} /></span>
                                </>
                            )}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}
